import { get2, put2 } from "@/shared/axios";
import useBranchId from "@/shared/hooks/useBranchId";
import { ITrackerSettings } from "@/shared/utils/interfaces";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { errorToast } from "../utils/helpers";
import useFranchiseHook from "./useFranchiseHook";

const useSystemTrackerHook = (customBranch?: number) => {
  const [franchise] = useFranchiseHook();
  const id = useBranchId();
  const branch = customBranch ?? id;
  const { t } = useTranslation();
  const systemTrackerQuery = useQuery<ITrackerSettings>({
    queryKey: ["orderTracker", branch, franchise],
    queryFn: () =>
      get2(`/internal/secure/pos/v2/orders/tracker/${franchise}/${branch}`),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: branch !== undefined
  });

  useEffect(() => {
    if (systemTrackerQuery.isError) {
      const error = systemTrackerQuery.error as any;
      if (error?.response?.status === 404) {
        const message = t("orderTrackerSettingsNotFound");
        errorToast(message, "orderTrackerSettingsNotFound");
      }
    }
  }, [systemTrackerQuery.isError]);

  const putTrackerSettings = useMutation({
    mutationFn: (data: ITrackerSettings) => {
      return put2(`/internal/secure/pos/v2/orders/tracker/${branch}`, data);
    },
    onSuccess: async () => {
      systemTrackerQuery.refetch();
      toast.success(t("success.success"));
    },
    onError: async () => {
      errorToast();
    }
  });

  const showTipsOnLive = useMemo(
    () => systemTrackerQuery?.data?.is_tip_shown_in_live_tracker,
    [systemTrackerQuery?.data]
  );

  return {
    systemTrackerQuery,
    putTrackerSettings,
    showTipsOnLive
  };
};

export default useSystemTrackerHook;
